import React, {useEffect, useState} from "react";
import {observer} from "mobx-react";
import {Container, Card, Row, Col, Button, Spinner, Alert} from "react-bootstrap";
import HeroSection from "../../../../components/HeroSection.jsx";
import {reportsApi} from "../../../../api/reports.js";
import {Link} from "react-router-dom";

function AdminReportsHomePage() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [summary, setSummary] = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const data = await reportsApi.getOrderReports({});
                setSummary(data.summary);
                setError("");
            } catch (e) {
                console.error("Failed to load report summary", e);
                setError("Failed to load KPI summary.");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    return (
        <Container className="dashboard-shell">
            <HeroSection
                accent="admin"
                eyebrow="Reports"
                title="Insights & KPIs"
                subtitle="Quick view of performance with links to detailed reports."
            />
            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
            {loading && (
                <div className="d-flex align-items-center my-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading KPIs...</span>
                </div>
            )}
            {!loading && summary && (
                <Card className="dashboard-panel mt-3">
                    <Card.Header className="bg-white"><strong>At a glance</strong></Card.Header>
                    <Card.Body>
                        <Row className="g-3 text-center">
                            <Col md={3}>
                                <div className="small text-muted">Orders</div>
                                <div className="h4">{summary.totalOrders}</div>
                            </Col>
                            <Col md={3}>
                                <div className="small text-muted">Revenue</div>
                                <div className="h5">${summary.totalRevenue.toFixed(2)}</div>
                            </Col>
                            <Col md={3}>
                                <div className="small text-muted">Product Fees</div>
                                <div className="h6">${summary.totalProductFees.toFixed(2)}</div>
                            </Col>
                            <Col md={3}>
                                <div className="small text-muted">Service Fees</div>
                                <div className="h6">${summary.totalServiceFees.toFixed(2)}</div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            <Card className="dashboard-panel mt-3">
                <Card.Header className="bg-white"><strong>Available reports</strong></Card.Header>
                <Card.Body>
                    <Row className="g-3">
                        <ReportLink title="Revenue Summary" to="/admin/reports/revenue" description="Totals, fees, and taxes over a date range." />
                        <ReportLink title="Orders by Tax Authority" to="/admin/reports/by-taxauthority" description="Volume and revenue by municipality." />
                        <ReportLink title="Orders by Date" to="/admin/reports/by-date" description="Daily order counts and revenue." />
                        <ReportLink title="Orders by Vendor" to="/admin/reports/by-vendor" description="Volume and revenue by software vendor." />
                        <ReportLink title="Payouts (estimated)" to="/admin/reports/payouts" description="Payout-style totals by tax authority." />
                        <ReportLink title="Municipality Outage Report" to="/admin/reports/outages" description="Current online/offline status and reasons." />
                    </Row>
                </Card.Body>
            </Card>
        </Container>
    );
}

function ReportLink({title, to, description}) {
    return (
        <Col md={6}>
            <Card className="h-100">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="mb-1">{title}</h5>
                            <div className="text-muted small">{description}</div>
                        </div>
                        <Button as={Link} to={to} variant="outline-primary" size="sm">Open</Button>
                    </div>
                </Card.Body>
            </Card>
        </Col>
    );
}

export default observer(AdminReportsHomePage);
